import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { toast } from 'sonner';
import { Users, UserCheck, ShoppingBag, DollarSign, Plus, Cpu, CheckCircle, CreditCard, FileText, Pencil, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const defaultPlan = () => ({ id: '', name: '', price: '', duration_months: 1, products_count: 0, consultations_included: 0, features: '' });
const defaultBlog = () => ({ title: '', content: '', excerpt: '', category: 'wellness', tags: '', author: '', image_url: '', seo_keywords: '' });

const AdminDashboard = () => {
  const { getAuthHeader } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [products, setProducts] = useState([]);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [verifyDoctorDialog, setVerifyDoctorDialog] = useState(null);
  const [degreeVerifiedNote, setDegreeVerifiedNote] = useState('');
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planForm, setPlanForm] = useState(defaultPlan());
  const [showBlogDialog, setShowBlogDialog] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [blogForm, setBlogForm] = useState(defaultBlog());
  const [newProduct, setNewProduct] = useState({
    name: '',
    description: '',
    price: '',
    category: 'herbs',
    dosha_tags: [],
    image_url: 'https://images.unsplash.com/photo-1612192666309-f2da37265a8a',
    stock: '100',
  });

  useEffect(() => {
    fetchAdminData();
  }, []);

  const fetchAdminData = async () => {
    try {
      const headers = getAuthHeader();
      const [statsRes, usersRes, doctorsRes, productsRes, plansRes, blogsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/users`, { headers }),
        axios.get(`${API}/admin/doctors`, { headers }),
        axios.get(`${API}/products`, { headers }),
        axios.get(`${API}/admin/subscription-plans`, { headers }),
        axios.get(`${API}/admin/blogs`, { headers }),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setDoctors(doctorsRes.data || []);
      setProducts(productsRes.data);
      setSubscriptionPlans(plansRes.data || []);
      setBlogs(blogsRes.data || []);
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyDoctor = async (doctorId) => {
    try {
      await axios.put(
        `${API}/admin/doctors/${doctorId}/verify`,
        { degree_verified: true, degree_verified_note: degreeVerifiedNote || undefined },
        { headers: getAuthHeader() }
      );
      toast.success('Doctor verified');
      setVerifyDoctorDialog(null);
      setDegreeVerifiedNote('');
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to verify doctor');
    }
  };

  const openPlanEdit = (plan) => {
    setEditingPlan(plan);
    setPlanForm({
      id: plan.id,
      name: plan.name,
      price: plan.price,
      duration_months: plan.duration_months ?? 1,
      products_count: plan.products_count ?? 0,
      consultations_included: plan.consultations_included ?? 0,
      features: Array.isArray(plan.features) ? plan.features.join('\n') : (plan.features || ''),
    });
    setShowPlanDialog(true);
  };

  const handleSavePlan = async (e) => {
    e.preventDefault();
    const payload = {
      id: planForm.id.trim(),
      name: planForm.name.trim(),
      price: parseFloat(planForm.price) || 0,
      duration_months: parseInt(planForm.duration_months, 10) || 1,
      products_count: parseInt(planForm.products_count, 10) || 0,
      consultations_included: parseInt(planForm.consultations_included, 10) || 0,
      features: planForm.features.split(/\n/).map(s => s.trim()).filter(Boolean),
    };
    try {
      if (editingPlan) {
        await axios.put(`${API}/admin/subscription-plans/${editingPlan.id}`, payload, { headers: getAuthHeader() });
        toast.success('Plan updated');
      } else {
        await axios.post(`${API}/admin/subscription-plans`, payload, { headers: getAuthHeader() });
        toast.success('Plan created');
      }
      setShowPlanDialog(false);
      setEditingPlan(null);
      setPlanForm(defaultPlan());
      fetchAdminData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save plan');
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm('Delete this subscription plan?')) return;
    try {
      await axios.delete(`${API}/admin/subscription-plans/${planId}`, { headers: getAuthHeader() });
      toast.success('Plan deleted');
      fetchAdminData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const openBlogEdit = async (blog) => {
    if (blog) {
      try {
        const res = await axios.get(`${API}/blogs/${blog.id}`);
        const b = res.data;
        setBlogForm({
          title: b.title || '',
          content: b.content || '',
          excerpt: b.excerpt || '',
          category: b.category || 'wellness',
          tags: Array.isArray(b.tags) ? b.tags.join(', ') : (b.tags || ''),
          author: b.author || '',
          image_url: b.image_url || '',
          seo_keywords: Array.isArray(b.seo_keywords) ? b.seo_keywords.join(', ') : (b.seo_keywords || ''),
        });
        setEditingBlog(b);
      } catch {
        toast.error('Failed to load blog');
        return;
      }
    } else {
      setBlogForm(defaultBlog());
      setEditingBlog(null);
    }
    setShowBlogDialog(true);
  };

  const handleSaveBlog = async (e) => {
    e.preventDefault();
    const tags = blogForm.tags.split(',').map(s => s.trim()).filter(Boolean);
    const seo_keywords = blogForm.seo_keywords.split(',').map(s => s.trim()).filter(Boolean);
    try {
      if (editingBlog) {
        await axios.put(`${API}/admin/blogs/${editingBlog.id}`, {
          title: blogForm.title,
          content: blogForm.content,
          excerpt: blogForm.excerpt,
          category: blogForm.category,
          tags,
          author: blogForm.author,
          image_url: blogForm.image_url,
          seo_keywords,
        }, { headers: getAuthHeader() });
        toast.success('Blog updated');
      } else {
        await axios.post(`${API}/blogs`, {
          title: blogForm.title,
          content: blogForm.content,
          excerpt: blogForm.excerpt,
          category: blogForm.category,
          tags,
          author: blogForm.author,
          image_url: blogForm.image_url,
          seo_keywords,
        }, { headers: getAuthHeader() });
        toast.success('Blog created');
      }
      setShowBlogDialog(false);
      setEditingBlog(null);
      setBlogForm(defaultBlog());
      fetchAdminData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save blog');
    }
  };

  const handleDeleteBlog = async (blogId) => {
    if (!window.confirm('Delete this blog?')) return;
    try {
      await axios.delete(`${API}/admin/blogs/${blogId}`, { headers: getAuthHeader() });
      toast.success('Blog deleted');
      fetchAdminData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API}/products`,
        {
          ...newProduct,
          price: parseFloat(newProduct.price),
          stock: parseInt(newProduct.stock),
          dosha_tags: newProduct.dosha_tags.length > 0 ? newProduct.dosha_tags : ['vata', 'pitta', 'kapha'],
        },
        { headers: getAuthHeader() }
      );
      toast.success('Product created successfully');
      setShowProductDialog(false);
      setNewProduct({
        name: '',
        description: '',
        price: '',
        category: 'herbs',
        dosha_tags: [],
        image_url: 'https://images.unsplash.com/photo-1612192666309-f2da37265a8a',
        stock: '100',
      });
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to create product');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="container mx-auto px-6 lg:px-12 py-12" data-testid="admin-dashboard">
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>
            Admin Dashboard
          </h1>
          <p className="text-lg text-gray-600">Platform overview and management</p>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Users className="h-5 w-5 text-primary" />
                  <span>Users</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-primary">{stats.total_users}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <UserCheck className="h-5 w-5 text-primary" />
                  <span>Doctors</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-primary">{stats.total_doctors}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <ShoppingBag className="h-5 w-5 text-primary" />
                  <span>Orders</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-primary">{stats.total_orders}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <DollarSign className="h-5 w-5 text-primary" />
                  <span>Revenue</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-primary">₹{stats.total_revenue.toFixed(2)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Cpu className="h-5 w-5 text-primary" />
                  <span>AI Usage</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-primary">{stats.total_ai_calls ?? 0}</div>
                <p className="text-sm text-gray-600">calls · ${(stats.total_credits_used_usd ?? 0).toFixed(2)} est.</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Doctors Management */}
        {doctors.length > 0 && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Doctors</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {doctors.map((doc) => (
                  <div key={doc.id} className="flex justify-between items-center border-b pb-3 last:border-0">
                    <div>
                      <div className="font-medium">{doc.name}</div>
                      <div className="text-sm text-gray-600">{doc.email} · {doc.profile?.specialization ?? '—'}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {doc.profile?.verified ? (
                        <span className="text-xs bg-green-100 text-green-800 px-3 py-1 rounded-full flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" /> Verified
                        </span>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => setVerifyDoctorDialog(doc)} data-testid={`verify-doctor-${doc.id}`}>
                          Verify
                        </Button>
                      )}
                      {doc.profile?.degree_verified && (
                        <span className="text-xs text-gray-500">Degree verified</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Products Management */}
        <Card className="mb-8">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>Products</CardTitle>
              <Button onClick={() => setShowProductDialog(true)} data-testid="add-product-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Product
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {products.slice(0, 5).map((product) => (
                <div key={product.id} className="flex justify-between items-center border-b pb-3 last:border-0">
                  <div>
                    <div className="font-medium">{product.name}</div>
                    <div className="text-sm text-gray-600">Stock: {product.stock}</div>
                  </div>
                  <div className="font-semibold text-primary">₹{product.price}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Subscription Plans */}
        <Card className="mb-8">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-primary" />
                Subscription Plans
              </CardTitle>
              <Button onClick={() => { setEditingPlan(null); setPlanForm(defaultPlan()); setShowPlanDialog(true); }} data-testid="add-plan-btn">
                <Plus className="h-4 w-4 mr-2" /> Add Plan
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {subscriptionPlans.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No plans. Add one or they will be seeded from defaults.</p>
              ) : (
                subscriptionPlans.map((plan) => (
                  <div key={plan.id} className="flex justify-between items-center border-b pb-3 last:border-0">
                    <div>
                      <div className="font-medium">{plan.name}</div>
                      <div className="text-sm text-gray-600">₹{plan.price} · {plan.duration_months} mo · {plan.products_count} products · {plan.consultations_included} consultations</div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => openPlanEdit(plan)}><Pencil className="h-3 w-3" /></Button>
                      <Button size="sm" variant="outline" onClick={() => handleDeletePlan(plan.id)}><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Blog CMS */}
        <Card className="mb-8">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                Blogs (CMS)
              </CardTitle>
              <Button onClick={() => openBlogEdit(null)} data-testid="add-blog-btn">
                <Plus className="h-4 w-4 mr-2" /> Add Blog
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {blogs.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No blogs yet.</p>
              ) : (
                blogs.map((blog) => (
                  <div key={blog.id} className="flex justify-between items-center border-b pb-3 last:border-0">
                    <div>
                      <div className="font-medium">{blog.title}</div>
                      <div className="text-sm text-gray-600">{blog.category} · {blog.views ?? 0} views</div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => openBlogEdit(blog)}><Pencil className="h-3 w-3" /></Button>
                      <Button size="sm" variant="outline" onClick={() => handleDeleteBlog(blog.id)}><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Users List */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Users</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {users.slice(0, 10).map((user) => (
                <div key={user.id} className="flex justify-between items-center border-b pb-3 last:border-0">
                  <div>
                    <div className="font-medium">{user.name}</div>
                    <div className="text-sm text-gray-600">{user.email}</div>
                  </div>
                  <span className="text-xs bg-primary/10 text-primary px-3 py-1 rounded-full capitalize">
                    {user.role}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </main>

      <Footer />

      {/* Verify Doctor Dialog */}
      <Dialog open={!!verifyDoctorDialog} onOpenChange={(open) => !open && (setVerifyDoctorDialog(null), setDegreeVerifiedNote(''))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Verify doctor</DialogTitle>
          </DialogHeader>
          {verifyDoctorDialog && (
            <>
              <p className="text-sm text-gray-600">
                Verify <strong>{verifyDoctorDialog.name}</strong> and optionally confirm degree verification.
              </p>
              <div className="space-y-2">
                <Label htmlFor="degree-note">Degree verified note (optional)</Label>
                <Input
                  id="degree-note"
                  placeholder="e.g. Original degree verified"
                  value={degreeVerifiedNote}
                  onChange={(e) => setDegreeVerifiedNote(e.target.value)}
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setVerifyDoctorDialog(null)}>Cancel</Button>
                <Button onClick={() => handleVerifyDoctor(verifyDoctorDialog.id)}>Verify doctor</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Product Dialog */}
      <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
        <DialogContent data-testid="add-product-dialog">
          <DialogHeader>
            <DialogTitle>Add New Product</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateProduct} className="space-y-4">
            <div>
              <Label htmlFor="product-name">Product Name</Label>
              <Input
                id="product-name"
                value={newProduct.name}
                onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="product-desc">Description</Label>
              <textarea
                id="product-desc"
                className="w-full border border-gray-200 rounded-lg p-3"
                value={newProduct.description}
                onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="product-price">Price (₹)</Label>
                <Input
                  id="product-price"
                  type="number"
                  step="0.01"
                  value={newProduct.price}
                  onChange={(e) => setNewProduct({ ...newProduct, price: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="product-stock">Stock</Label>
                <Input
                  id="product-stock"
                  type="number"
                  value={newProduct.stock}
                  onChange={(e) => setNewProduct({ ...newProduct, stock: e.target.value })}
                  required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="product-category">Category</Label>
              <select
                id="product-category"
                className="w-full border border-gray-200 rounded-lg h-10 px-3"
                value={newProduct.category}
                onChange={(e) => setNewProduct({ ...newProduct, category: e.target.value })}
              >
                <option value="herbs">Herbs</option>
                <option value="supplements">Supplements</option>
                <option value="oils">Oils</option>
                <option value="teas">Teas</option>
                <option value="skincare">Skincare</option>
              </select>
            </div>
            <Button type="submit" className="w-full">
              Create Product
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Subscription Plan Dialog */}
      <Dialog open={showPlanDialog} onOpenChange={(open) => !open && (setShowPlanDialog(false), setEditingPlan(null), setPlanForm(defaultPlan()))}>
        <DialogContent className="max-w-lg" data-testid="plan-dialog">
          <DialogHeader>
            <DialogTitle>{editingPlan ? 'Edit plan' : 'Add subscription plan'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSavePlan} className="space-y-4">
            <div>
              <Label htmlFor="plan-id">Plan ID (e.g. starter)</Label>
              <Input id="plan-id" value={planForm.id} onChange={(e) => setPlanForm({ ...planForm, id: e.target.value })} required disabled={!!editingPlan} placeholder="starter" />
            </div>
            <div>
              <Label htmlFor="plan-name">Name</Label>
              <Input id="plan-name" value={planForm.name} onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })} required placeholder="Starter Wellness Kit" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="plan-price">Price (₹)</Label>
                <Input id="plan-price" type="number" step="0.01" value={planForm.price} onChange={(e) => setPlanForm({ ...planForm, price: e.target.value })} required />
              </div>
              <div>
                <Label htmlFor="plan-duration">Duration (months)</Label>
                <Input id="plan-duration" type="number" min="1" value={planForm.duration_months} onChange={(e) => setPlanForm({ ...planForm, duration_months: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="plan-products">Products count</Label>
                <Input id="plan-products" type="number" min="0" value={planForm.products_count} onChange={(e) => setPlanForm({ ...planForm, products_count: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="plan-consultations">Consultations included</Label>
                <Input id="plan-consultations" type="number" min="0" value={planForm.consultations_included} onChange={(e) => setPlanForm({ ...planForm, consultations_included: e.target.value })} />
              </div>
            </div>
            <div>
              <Label htmlFor="plan-features">Features (one per line)</Label>
              <textarea id="plan-features" className="w-full border rounded-lg p-2 min-h-[80px]" value={planForm.features} onChange={(e) => setPlanForm({ ...planForm, features: e.target.value })} placeholder="Feature 1&#10;Feature 2" />
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowPlanDialog(false)}>Cancel</Button>
              <Button type="submit">{editingPlan ? 'Update' : 'Create'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Blog CMS Dialog */}
      <Dialog open={showBlogDialog} onOpenChange={(open) => !open && (setShowBlogDialog(false), setEditingBlog(null), setBlogForm(defaultBlog()))}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="blog-dialog">
          <DialogHeader>
            <DialogTitle>{editingBlog ? 'Edit blog' : 'Add blog'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveBlog} className="space-y-4">
            <div>
              <Label htmlFor="blog-title">Title</Label>
              <Input id="blog-title" value={blogForm.title} onChange={(e) => setBlogForm({ ...blogForm, title: e.target.value })} required />
            </div>
            <div>
              <Label htmlFor="blog-excerpt">Excerpt</Label>
              <Input id="blog-excerpt" value={blogForm.excerpt} onChange={(e) => setBlogForm({ ...blogForm, excerpt: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="blog-content">Content</Label>
              <textarea id="blog-content" className="w-full border rounded-lg p-3 min-h-[120px]" value={blogForm.content} onChange={(e) => setBlogForm({ ...blogForm, content: e.target.value })} required />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="blog-category">Category</Label>
                <Input id="blog-category" value={blogForm.category} onChange={(e) => setBlogForm({ ...blogForm, category: e.target.value })} placeholder="wellness" />
              </div>
              <div>
                <Label htmlFor="blog-author">Author</Label>
                <Input id="blog-author" value={blogForm.author} onChange={(e) => setBlogForm({ ...blogForm, author: e.target.value })} />
              </div>
            </div>
            <div>
              <Label htmlFor="blog-image">Image URL</Label>
              <Input id="blog-image" value={blogForm.image_url} onChange={(e) => setBlogForm({ ...blogForm, image_url: e.target.value })} placeholder="https://..." />
            </div>
            <div>
              <Label htmlFor="blog-tags">Tags (comma-separated)</Label>
              <Input id="blog-tags" value={blogForm.tags} onChange={(e) => setBlogForm({ ...blogForm, tags: e.target.value })} placeholder="ayurveda, wellness" />
            </div>
            <div>
              <Label htmlFor="blog-seo">SEO keywords (comma-separated)</Label>
              <Input id="blog-seo" value={blogForm.seo_keywords} onChange={(e) => setBlogForm({ ...blogForm, seo_keywords: e.target.value })} />
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowBlogDialog(false)}>Cancel</Button>
              <Button type="submit">{editingBlog ? 'Update' : 'Create'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;